import React, { useState, useMemo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Modal, AutoComplete, Button, Typography, notification } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import _ from "lodash";
import { successAction, errorAction, types as at } from "@authzsvc/api-lib";
import { searchForPerson } from "../../actions";
import { ContentTable } from "@authzsvc/common-react-components";

const { Text } = Typography;

const PersonTable = ({ dataSource, searchLoading, handleSelect }) => {
  const columns = [
    { title: "Name", dataIndex: "displayName", searchable: true },
    {
      title: "Login (UPN)",
      dataIndex: "upn",
      searchable: true,
      render: (val, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          {val ? <Text code>{val}</Text> : <Text type="secondary">N/A</Text>}
          <Button
            id="selectPersonButton"
            color="green"
            variant="filled"
            icon={<CheckOutlined />}
            onClick={() => handleSelect(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <ContentTable
      style={{ marginTop: 10 }}
      loading={searchLoading}
      rowKey="id"
      pagination={{ pageSize: 10 }}
      columns={columns}
      dataSource={dataSource}
    />
  );
};

PersonTable.propTypes = {
  dataSource: PropTypes.array,
  searchLoading: PropTypes.bool,
  handleSelect: PropTypes.func,
};

const PersonSearchModal = ({
  title,
  searchForPerson,
  onSelect,
  open,
  onOpenChange,
}) => {
  const [searchLoading, setSearchLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    if (open) {
      setDataSource([]);
      setSearchLoading(false);
    }
  }, [open]);

  const clear = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  const handleSelect = useCallback(
    (record) => {
      onSelect?.(record);
      clear();
    },
    [onSelect, clear],
  );

  const debouncedSearch = useMemo(
    () =>
      _.debounce(async (value = "") => {
        const q = value.trim();
        if (q.length < 2) {
          setDataSource([]);
          setSearchLoading(false);
          return;
        }
        setSearchLoading(true);
        try {
          const action = await searchForPerson(q);
          if (action?.type === successAction(at.SEARCH_IDENTITY)) {
            setDataSource(Array.isArray(action.payload) ? action.payload : []);
          } else if (action?.type === errorAction(at.SEARCH_IDENTITY)) {
            notification.error({
              message: action.payload?.title || "Search failed",
              description:
                action.payload?.data?.message ||
                "Unable to search for identities.",
            });
            setDataSource([]);
          } else {
            setDataSource([]);
          }
        } finally {
          setSearchLoading(false);
        }
      }, 400),
    [searchForPerson],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  return (
    <Modal
      centered
      title={title}
      open={!!open}
      onCancel={clear}
      footer={null}
      width={700}
      closable
    >
      <AutoComplete
        onChange={debouncedSearch}
        onSearch={debouncedSearch}
        allowClear
        options={[]}
        style={{ width: "100%" }}
        placeholder="Type at least 2 characters of the identity's email, login or name."
      />
      <PersonTable
        dataSource={dataSource}
        searchLoading={searchLoading}
        handleSelect={handleSelect}
      />
    </Modal>
  );
};

PersonSearchModal.propTypes = {
  title: PropTypes.string.isRequired,
  searchForPerson: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
};

export default connect(() => ({}), { searchForPerson })(PersonSearchModal);
